import { z } from "zod";
import { AssessmentEnvelopeSchema } from "./assessment";

export const ShadowDepthSchema = z.enum(["Signal", "Trace", "Echo", "Mirror", "Twin", "SHADOW"]);

export const ArtistStatusSchema = z.enum([
  "shadow_user",
  "verified_shadow_artist",
  "dark_room_recommended",
  "studio_featured",
  "vaultspark_studios_artist"
]);

export const PrivacyLevelSchema = z.enum(["public_only", "private_allowed", "full_catalog_allowed"]);

export const ArtistIntakeSchema = z.object({
  artistName: z.string().min(1),
  email: z.string().email(),
  genre: z.string().optional(),
  links: z.array(z.string().url()).default([]),
  goals: z.array(z.string()).default([]),
  aiComfortLevel: z.enum(["analysis_only", "private_tools_ok", "full_ai_allowed"]).default("analysis_only"),
  critiqueMode: z.enum(["direct", "constructive", "mentor"]).default("direct"),
  privacyLevel: PrivacyLevelSchema.default("public_only"),
  songText: z.string().optional(),
  previousRelease: z.object({
    title: z.string().min(1),
    releasedAt: z.string().min(1),
    platform: z.string().min(1)
  }).optional()
});

/** Local preparation needs no contact address. Server intake retains its separate required-email contract. */
export const LocalArtistIntakeSchema = ArtistIntakeSchema.extend({
  email: z.union([z.literal(""), z.string().email().max(320)]).default("")
});

export const ConsentAcceptanceSchema = z.object({
  analysisConsent: z.boolean(),
  rightsAttestation: z.boolean(),
  aiDisclosureAcknowledged: z.boolean(),
  marketingOptIn: z.boolean().default(false),
  benchmarkOptIn: z.boolean().default(false),
  version: z.string().default("shadow-consent-v1")
});

export const PersonaReviewSchema = z.object({
  persona: z.string(),
  score: z.number().min(0).max(100),
  verdict: z.string(),
  critique: z.string().optional(),
  strengths: z.array(z.string()),
  weaknesses: z.array(z.string()),
  action: z.string(),
  warning: z.string(),
  confidence: z.enum(["low", "medium", "high"])
});

export const DarkRoomReviewSchema = z.object({
  id: z.string(),
  artistId: z.string(),
  artistName: z.string(),
  reviewType: z.enum(["dark_room_lite", "dark_room_full"]),
  assessment: AssessmentEnvelopeSchema.optional(),
  personaReviews: z.array(PersonaReviewSchema),
  compositeScores: z.object({
    music: z.number().optional(),
    identity: z.number().optional(),
    catalog: z.number().optional(),
    market: z.number().optional(),
    readiness: z.number().optional(),
    studioFit: z.number().optional(),
    overall: z.number()
  }),
  confidence: z.enum(["low", "medium", "high"]),
  recommendation: z.object({
    suggestedDepth: ShadowDepthSchema,
    artistStatus: ArtistStatusSchema,
    escalationFlags: z.array(z.string()),
    topActions: z.array(z.string()),
    missingInfo: z.array(z.string())
  }),
  seamlineDraft: z.object({
    artistId: z.string(),
    artistName: z.string(),
    headline: z.string(),
    bioShort: z.string(),
    bioLong: z.string(),
    links: z.array(z.string()),
    featuredTrack: z.string().nullable(),
    theme: z.string(),
    cta: z.string(),
    shadowBadge: z.boolean(),
    proofItems: z.array(z.string()),
    privacy: z.enum(["public", "private"])
  }),
  synthaHandoff: z.object({
    artistId: z.string(),
    status: z.enum(["draft", "blocked", "ready_for_review"]),
    validationErrors: z.array(z.string()),
    includeAudio: z.boolean()
  })
});

export const FeedbackActionStatusSchema = z.enum(["open", "planned", "done"]);

export const ShadowFeedbackEventSchema = z.object({
  artistId: z.string().min(1),
  reviewId: z.string().min(1),
  missingInfo: z.array(z.string()).default([]),
  nextActionStatus: z.array(
    z.object({
      action: z.string().min(1),
      status: FeedbackActionStatusSchema
    })
  ),
  upgradeIntent: z.string().min(1),
  releaseRoomStatus: z.enum(["blocked", "draft", "ready_for_review"]),
  createdAt: z.string().datetime().optional()
});

export const ShadowAnalyticsEventSchema = z.object({
  source: z.enum(["server_observed", "client_reported_unverified"]).optional(),
  eventName: z.enum([
    "shadow_landing_view",
    "shadow_signal_started",
    "shadow_signal_completed",
    "dark_room_lite_generated",
    "signal_capsule_exported",
    "signal_receipt_emailed",
    "signal_receipt_imported",
    "release_room_opened",
    "seamline_draft_created",
    "syntha_handoff_created",
    "depth_recommendation_viewed",
    "upgrade_clicked",
    "artist_flagged_high_potential",
    "artist_flagged_high_risk",
    "consent_required_block",
    "contact_submitted",
    "signal_feedback_submitted",
    "genre_benchmark_view",
    "benchmark_opted_in"
  ]),
  artistId: z.string().optional(),
  reviewId: z.string().optional(),
  route: z.string().optional(),
  metadata: z.record(z.union([z.string(), z.number(), z.boolean(), z.undefined()])).optional(),
  createdAt: z.string().datetime().optional()
});

export const ReleasePackageDraftSchema = z.object({
  artistId: z.string().min(1),
  reviewId: z.string().min(1),
  headline: z.string().min(1),
  shortBio: z.string().min(1),
  proofItems: z.array(z.string()).default([]),
  rightsNotes: z.string().min(1),
  disclosureNotes: z.string().min(1),
  releaseNotes: z.string().min(1),
  handoffStatus: z.enum(["blocked", "draft", "ready_for_review"]),
  completeness: z.number().min(0).max(100),
  createdAt: z.string().datetime().optional()
});

export const AdminQueueItemSchema = z.object({
  artistId: z.string().min(1),
  reviewId: z.string().min(1),
  artistName: z.string().min(1),
  email: z.string().email().optional(),
  overallScore: z.number().min(0).max(100),
  confidence: z.enum(["low", "medium", "high"]),
  suggestedDepth: ShadowDepthSchema,
  riskFlags: z.array(z.string()).default([]),
  missingInfo: z.array(z.string()).default([]),
  releaseRoomStatus: z.enum(["blocked", "draft", "ready_for_review"]),
  studioGateDecision: z.enum(["hold", "package", "review"]),
  studioGateRiskLevel: z.enum(["low", "medium", "high"]),
  requestedAction: z.enum(["request_info", "invite_upgrade", "prepare_syntha_handoff", "block_distribution"]),
  status: z.enum(["new", "in_review", "waiting_on_artist", "approved", "blocked"]).default("new"),
  updatedAt: z.string()
});

export const AdminQueueActionSchema = z.object({
  reviewId: z.string().min(1),
  status: AdminQueueItemSchema.shape.status,
  requestedAction: AdminQueueItemSchema.shape.requestedAction.optional(),
  studioGateDecision: AdminQueueItemSchema.shape.studioGateDecision.optional(),
  studioGateRiskLevel: AdminQueueItemSchema.shape.studioGateRiskLevel.optional(),
  note: z.string().trim().max(1000).optional(),
  actor: z.string().trim().min(1).max(120).default("shadow-admin"),
  createdAt: z.string().datetime().optional()
});

export type ArtistIntake = z.infer<typeof ArtistIntakeSchema>;
export type ConsentAcceptance = z.infer<typeof ConsentAcceptanceSchema>;
export type DarkRoomReview = z.infer<typeof DarkRoomReviewSchema>;
export type PersonaReview = z.infer<typeof PersonaReviewSchema>;
export type ShadowFeedbackEvent = z.infer<typeof ShadowFeedbackEventSchema>;
export type ShadowAnalyticsEvent = z.infer<typeof ShadowAnalyticsEventSchema>;
export type ReleasePackageDraft = z.infer<typeof ReleasePackageDraftSchema>;
export type AdminQueueItem = z.infer<typeof AdminQueueItemSchema>;
export type AdminQueueAction = z.infer<typeof AdminQueueActionSchema>;
